/**
 * Snapshot interpolation — PURE. See `wp6-client-render.md`
 * § "Interpolation rules".
 *
 *   • Position: lerp `a → b` by `alpha ∈ [0, 1]`.
 *   • Facing: SNAP to `b`. Direction lerping looks drunk.
 *   • hp / maxHp: snap to `b`. Health bars tick.
 *   • cast state / cooldowns / gcd / targets / phase / winner: snap to `b`.
 *   • Entity in `b` but not in `a`: draw at `b` (can't happen at MVP —
 *     fixed roster — but the rule is total).
 *   • Walls (#488): SNAP to `b` — a wall never moves during its lifetime,
 *     so there's nothing to lerp (unlike projectiles' position). Present in
 *     `b` → draw; dropped from `b` (expired) → removed, same as a
 *     hit/expired projectile.
 *   • NEVER extrapolate — alpha is clamped to [0, 1].
 *
 * Inputs are treated as read-only (WireSimState is already a strict
 * value from the parser). Output is a NEW array; entities are new
 * objects. No mutation of inputs.
 */

import type { WireSimState } from "@pit/protocol";
import type { RenderWorldPos } from "./projection.js";

/**
 * Per-entity interpolated view — the SHAPE the stage consumes when
 * building/updating display objects.
 *
 * `RenderWorldPos` (float `wx`/`wy`) is intentionally structurally
 * distinct from wire `{ x, y }` (branded `Units`) so float world-space
 * coords can't be handed back into protocol code.
 */
export interface InterpolatedEntity {
  readonly entityId: WireSimState["entities"][number]["entityId"];
  readonly seat: WireSimState["entities"][number]["seat"];
  readonly team: WireSimState["entities"][number]["team"];
  readonly pos: RenderWorldPos;
  readonly facing: WireSimState["entities"][number]["facing"];
  readonly hp: WireSimState["entities"][number]["hp"];
  readonly maxHp: WireSimState["entities"][number]["maxHp"];
  readonly alive: boolean;
  readonly cast: WireSimState["entities"][number]["cast"];
  readonly gcdReadyAtTick: WireSimState["entities"][number]["gcdReadyAtTick"];
  readonly cooldowns: WireSimState["entities"][number]["cooldowns"];
  readonly targets: WireSimState["entities"][number]["targets"];
  /** Castable resource pool balance (#89/#266), snapped like hp/maxHp (#337 —
   *  the above-head resource bar reads this). `null` for a pool-less champion. */
  readonly resource: WireSimState["entities"][number]["resource"];
  /** Builder/spender point pool balance (#90/#266), snapped like hp/maxHp. */
  readonly points: WireSimState["entities"][number]["points"];
  /** Live, aura-modified body radius (#342), snapped like hp/maxHp (a size
   *  change lands on a specific tick, same as a stat-mod aura applying —
   *  interpolating it would smear the size change across a frame instead of
   *  matching the sim's discrete tick-to-tick change). */
  readonly radius: WireSimState["entities"][number]["radius"];
  /** Active auras (#30), snapped like hp/maxHp — never interpolated (an aura
   *  either holds or it doesn't on a given tick). #422: the render layer
   *  reads this (via `../input/stealthVisibility.js`, NOT a direct
   *  `@pit/protocol` runtime import — see that module's doc comment) to
   *  suppress an enemy-team stealthed entity's sprite/bars/nameplate. */
  readonly auras: WireSimState["entities"][number]["auras"];
}

/**
 * Per-projectile interpolated view (#29). Position lerps between snapshots
 * like entities; a projectile present in `b` but not `a` (just spawned) draws
 * at `b`; one present in `a` but not `b` (hit/expired) is simply dropped —
 * NEVER extrapolated past its last known position.
 */
export interface InterpolatedProjectile {
  readonly projectileId: WireSimState["projectiles"][number]["projectileId"];
  /** Spell identity snaps to the newer snapshot independently of ownership
   *  (reflection changes allegiance without changing the projectile's style). */
  readonly abilityId: WireSimState["projectiles"][number]["abilityId"];
  readonly ownerSeat: WireSimState["projectiles"][number]["ownerSeat"];
  readonly ownerTeam: WireSimState["projectiles"][number]["ownerTeam"];
  readonly pos: RenderWorldPos;
  readonly dx: WireSimState["projectiles"][number]["dx"];
  readonly dy: WireSimState["projectiles"][number]["dy"];
  readonly radius: WireSimState["projectiles"][number]["radius"];
}

/**
 * One circular sub-obstacle of an interpolated wall — the wire's row-of-circles
 * geometry, projected to a float render position (#488). Mirrors
 * `InterpolatedProjectile`'s `pos` treatment.
 */
export interface InterpolatedWallObstacle {
  readonly pos: RenderWorldPos;
  readonly radius: WireSimState["walls"][number]["obstacles"][number]["radius"];
}

/**
 * Per-wall interpolated view (#488, consuming #129's `spawnWall` subsystem).
 * A wall's sub-obstacles are STATIC for its lifetime (walls never move), so
 * there is nothing to lerp — every wall present in `b` draws at its `b`
 * geometry, mirroring the "snap to `b`" rule the doctrine already uses for
 * facing/hp/auras. A wall present in `a` but not `b` (expired) is simply
 * dropped, same as a hit/expired projectile.
 */
export interface InterpolatedWall {
  readonly wallId: WireSimState["walls"][number]["wallId"];
  readonly ownerSeat: WireSimState["walls"][number]["ownerSeat"];
  readonly ownerTeam: WireSimState["walls"][number]["ownerTeam"];
  readonly obstacles: readonly InterpolatedWallObstacle[];
}

/** Interpolated world state — floats permitted (presentation only). */
export interface InterpolatedState {
  readonly phase: WireSimState["phase"];
  readonly winner: WireSimState["winner"];
  /** Float render-tick coord; not a sim tick. */
  readonly renderTick: number;
  readonly entities: readonly InterpolatedEntity[];
  readonly projectiles: readonly InterpolatedProjectile[];
  readonly walls: readonly InterpolatedWall[];
}

/** Clamp helper (float; presentation layer). */
function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

/**
 * Interpolate between two snapshots at `alpha ∈ [0, 1]`. `alpha` is
 * CLAMPED — extrapolation is banned per the doctrine (mispredicted
 * corpses sliding around after death).
 *
 * `renderTick` is the float render-time coordinate (usually `a.tick +
 * clampedAlpha`); callers pass it through so the stage can render
 * smooth cast bars using this same clock without recomputing.
 *
 * `a === b` (single snapshot, no pair) is a legal degenerate case
 * (start-of-match, gap): position collapses to `b` (== `a`).
 */
export function interpolate(
  a: WireSimState,
  b: WireSimState,
  alpha: number,
  renderTick: number,
): InterpolatedState {
  const t = clamp01(alpha);

  // Build a lookup for `a`'s entities by id so we can match; entity
  // rosters are stable at MVP (fixed 2 seats), but the rule is total.
  const aById = new Map<string, WireSimState["entities"][number]>();
  for (const e of a.entities) {
    aById.set(e.entityId, e);
  }

  const entities: InterpolatedEntity[] = b.entities.map((be) => {
    const ae = aById.get(be.entityId);
    if (ae === undefined) {
      // In `b` but not in `a`: draw at `b`. (See doctrine.)
      return toInterpolatedEntity(be, { wx: be.x, wy: be.y });
    }
    // Position: lerp. Everything else: snap to `b`.
    const wx = lerpFloat(ae.x, be.x, t);
    const wy = lerpFloat(ae.y, be.y, t);
    return toInterpolatedEntity(be, { wx, wy });
  });

  // Projectiles (#29): lerp position for those present in BOTH snapshots;
  // draw just-spawned ones at `b`; drop ones that vanished (hit/expired).
  const aProjById = new Map<string, WireSimState["projectiles"][number]>();
  for (const p of a.projectiles) {
    aProjById.set(p.projectileId, p);
  }
  const projectiles: InterpolatedProjectile[] = b.projectiles.map((bp) => {
    const ap = aProjById.get(bp.projectileId);
    const pos: RenderWorldPos =
      ap === undefined
        ? { wx: bp.x, wy: bp.y }
        : { wx: lerpFloat(ap.x, bp.x, t), wy: lerpFloat(ap.y, bp.y, t) };
    return {
      projectileId: bp.projectileId,
      abilityId: bp.abilityId,
      ownerSeat: bp.ownerSeat,
      ownerTeam: bp.ownerTeam,
      pos,
      dx: bp.dx,
      dy: bp.dy,
      radius: bp.radius,
    };
  });

  // Walls (#488): snap to `b` — walls never move during their lifetime, so
  // there's nothing to lerp (unlike projectiles). Present in `b` → draw;
  // absent from `b` (expired the tick after `expiresAtTick`) → dropped.
  const walls: InterpolatedWall[] = b.walls.map((bw) => ({
    wallId: bw.wallId,
    ownerSeat: bw.ownerSeat,
    ownerTeam: bw.ownerTeam,
    obstacles: bw.obstacles.map((o) => ({
      pos: { wx: o.x, wy: o.y },
      radius: o.radius,
    })),
  }));

  return {
    phase: b.phase,
    winner: b.winner,
    renderTick,
    entities,
    projectiles,
    walls,
  };
}

function toInterpolatedEntity(
  be: WireSimState["entities"][number],
  pos: RenderWorldPos,
): InterpolatedEntity {
  return {
    entityId: be.entityId,
    seat: be.seat,
    team: be.team,
    pos,
    facing: be.facing,
    hp: be.hp,
    maxHp: be.maxHp,
    alive: be.alive,
    cast: be.cast,
    gcdReadyAtTick: be.gcdReadyAtTick,
    cooldowns: be.cooldowns,
    targets: be.targets,
    resource: be.resource,
    points: be.points,
    radius: be.radius,
    auras: be.auras,
  };
}

function lerpFloat(x0: number, x1: number, t: number): number {
  return x0 + (x1 - x0) * t;
}
